import React, { useContext, useEffect, useState } from 'react';
import { Button } from '../../elements';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    couponType,
    MOBILE_MODAL_COUPON,
    REPLACE_COUPON_EVENTS,
    UPDATE_COUPON_MESSAGE
} from '../../../store/spirs/actions';
import { StoreType } from '../../../store/spirs/types';
import { CouponState } from '../../../store/spirs/reducers/couponReducer';
import iziToast from 'izitoast';
import { updateCoupons } from '../../../spirsApi/api-client';
import { compareEvents } from '../utils';
import { CouponStepForm, EventForm } from '../../../models/api/codegen/code';
import { getMinOrMaxLimit } from './components/StepsCompute';
import { calculateExpressOdds, calculateSystemOdds, multipleRateWithFixed, undefinedToZero } from '../OddsUtils';
import { AuthContext } from '../../context/AuthContext';
import { useTestOverlay } from '../TestOverlayContext';
import { useNavigate } from 'react-router-dom';
import { NoAuthOrMoneyModal } from '../../modal/noAuthOrMoneyModal/NoAuthOrMoneyModal';
import { ModalType } from '../../modal/noAuthOrMoneyModal/ModalType';

interface AcceptBlockProps {
    removeAllItems: () => void;
}

export function AcceptBlock(props: AcceptBlockProps) {
    const {t} = useTranslation();
    const authContext = useContext(AuthContext);
    const dispatch = useDispatch();
    const currentCouponType = useSelector((state: StoreType) => state.spirs.coupons.currentCouponType);
    const events = useSelector((state: StoreType) => state.spirs.coupons[currentCouponType]);
    const [mustApprove, setMustApprove] = useState<boolean>(false);
    const getBalance = (state: StoreType) => String(state.spirs.playerInfo.balance);
    const balance = useSelector((state: StoreType) => getBalance(state));
    const [maxEraiCoeff, setMaxEraiCoeff] = useState(false);
    const [maxEraiResult, setMaxEraiResult] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const { saveCouponWithWebSocket } = useTestOverlay();
    const isSubmitting = React.useRef(false);
    const playerIdentified = useSelector(
        (state: StoreType) => state.spirs.playerIdentified
    );

    const playerAuthenticated = useSelector(
        (state: StoreType) => state.spirs.playerAuthenticated
    );

    const playerHasAccounts = useSelector(
        (state: StoreType) => state.spirs.playerHasAccounts
    );

    const {
        spirs: {
            playerInfo,
            isGetLimits,
            coupons,
            isLoadingCoupons,
            playerSettings: {acceptType}
        }
    } = useSelector((state: StoreType) => state);

    useEffect(() => {
        let currentMaxOdds = 0;
        let currentMaxResult = "0";
        switch (currentCouponType) {
            case "ORDINAR":
                currentMaxOdds = Math.max.apply(null, events.map(el => el.odds));
                let z = 0;
                for (let el of events) {
                    z += undefinedToZero(el.rate) * undefinedToZero(el.odds);
                }
                currentMaxResult = z.toFixed(2);
                break;
            case "EXPRESS":
                currentMaxOdds = Number((
                    events.reduce((total, amount) => {
                        return total * amount.odds
                    }, 1)
                ).toFixed(2));
                currentMaxResult = multipleRateWithFixed(calculateExpressOdds(events), coupons.rate.EXPRESS);
                break;
            case "SYSTEM":
                const activeEvents = coupons[currentCouponType].filter(el => el.active);
                const oddsArray = activeEvents.map(el => el.odds).filter((odds): odds is number => !(odds === undefined || odds === null));
                currentMaxOdds = oddsForSystem(oddsArray);
                currentMaxResult = multipleRateWithFixed(calculateSystemOdds(events), coupons.rate.SYSTEM);
                break;
            default: throw Error('not implemented ' + currentCouponType)
        }
        if (!authContext.needAuth && playerInfo.playerId && playerInfo.playerId !== "GUEST") {
            setMaxEraiCoeff(currentMaxOdds > (playerInfo.maxEraiCoeff || 0));
            setMaxEraiResult(Number(currentMaxResult) > (playerInfo.maxEraiResult || 0));
        }
    }, [events, currentCouponType]);

    useEffect(() => {

        const intervalId = setInterval(() => {
                updateCoupons({
                    couponForm: {
                        playerId: playerInfo.playerId,
                        steps: getStepsByCouponType(coupons.currentCouponType),
                        acceptType: acceptType
                    }
                }).then(value => {
                    if (value && value.steps && value.steps.length) {
                        // отличаем оридинар от системы и экспресса, по первому элементу массива
                        const eventsType = value.steps[0].type;
                        const compared = compareEvents(eventsType, value.steps, events);
                        dispatch(REPLACE_COUPON_EVENTS(compared.outputEvents));
                        if (maxEraiCoeff || maxEraiResult) return;
                        if (compared.oddsChanged) {
                            dispatch(UPDATE_COUPON_MESSAGE(t("oddsUpdated")));

                            if (compared.allEventsDirection) {
                                switch (acceptType) {
                                    case "ACCEPT_ANY_CHANGES":
                                        setMustApprove(false);
                                        break;
                                    case "ACCEPT_PROMOTION":
                                        compared.allEventsDirection !== 'up' && setMustApprove(true);
                                        break;
                                    case "DO_NOT_ACCEPT_CHANGES":
                                        setMustApprove(true);
                                        break;
                                }
                            }
                        }

                    }
                }).catch(() => console.log('Error while handle updateCoupons request...'))
            },
            2000);

        return () => clearInterval(intervalId);

    }, [coupons]);

    const messageError = (message: string) => {

        iziToast.info({
            backgroundColor:'#383E45',
            messageColor:'rgba(255, 255, 255, 0.85)',
            maxWidth: 414,
            message: `
                    <div class="flex align-senter px-[20px] py-[15px] rounded-[4px]">
            <div class="flex-shrink-0 mr-[15px]"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M12 0C18.6281 0 24 5.37187 24 12C24 18.6281 18.6281 24 12 24C5.37187 24 0 18.6281 0 12C0 5.37187 5.37187 0 12 0ZM9.67969 7.48828C9.21328 7.48828 8.83594 7.86562 8.83594 8.33203C8.83594 8.79844 9.21328 9.17578 9.67969 9.17578H11.0859V16.5371H9.08887C8.62268 16.5372 8.24533 16.9147 8.24512 17.3809C8.24512 17.8472 8.62255 18.2245 9.08887 18.2246H14.7705C15.2368 18.2245 15.6143 17.8472 15.6143 17.3809C15.614 16.9147 15.2367 16.5372 14.7705 16.5371H12.7734V8.33203C12.7734 7.86562 12.3961 7.48828 11.9297 7.48828H9.67969ZM11.9297 4.32422C11.4633 4.32422 11.0859 4.70156 11.0859 5.16797C11.0859 5.63438 11.4633 6.01172 11.9297 6.01172C12.3961 6.01172 12.7734 5.63438 12.7734 5.16797C12.7734 4.70156 12.3961 4.32422 11.9297 4.32422Z" fill="white" fill-opacity="0.65"/>
</svg></div>
            <div class="text-[14px] font-normal">${t(message)}</div>
        </div>
    `,
            position: "topCenter",
            progressBar: false,
            messageSize: '14px',
            closeOnClick: true,
            onOpening: () => {
                const toast = document.querySelector('.iziToast') as HTMLElement;
                const toastBody = document.querySelector('.iziToast-body') as HTMLElement;
                const toastClose = document.querySelector('.iziToast-close') as HTMLElement;
                toastClose.style.display = "none";
                toastBody.style.padding = '0';
                toast.style.zIndex = "10000";
                toast.style.position = "fixed";
                toast.style.top = "50%";
                toast.style.left = "50%";
                toast.style.transform = "translate(-50%, -50%)";
            }
        });
    };

    async function saveCouponNow() {
        // авторизованный но не идентифицированный пользователь
        if (playerAuthenticated && !playerIdentified) {
            setOpenModal(true);
            return;
        }

        if (playerAuthenticated && playerIdentified && !playerHasAccounts) {
            setOpenModal(true);
            return;
        }


        // неавторизованный в ЛК
        if (authContext.needAuth) {
            dispatch(MOBILE_MODAL_COUPON(false));
            return authContext.onAuth(t("coupons.saveUnauthorized"));
        }

        dispatch(UPDATE_COUPON_MESSAGE(''));
        if (isLoadingCoupons) {
            console.warn('isLoadingCoupons is true...');
            return;
        }

        let steps = getStepsByCouponType(coupons.currentCouponType);
        if (currentCouponType === 'EXPRESS' && events.length < 2) {
            messageError('coupons.EXPRESS.error');
            return;
        }
        if (currentCouponType === 'SYSTEM' && events.length < 3) {
            messageError('coupons.SYSTEM.error');
            return;
        }

        const checkOrdinar = coupons.currentCouponType === 'ORDINAR' && steps.length > 0;
        const multyStep = coupons.currentCouponType !== 'ORDINAR' && steps.filter(step => (step.events as EventForm[]).length > 0).length > 0;
        const checks = {
            ['ORDINAR' as CouponState['currentCouponType']]: checkOrdinar,
            ['SYSTEM' as CouponState['currentCouponType']]: multyStep,
            ['EXPRESS' as CouponState['currentCouponType']]: multyStep
        };
        if (!checks[coupons.currentCouponType]) {
            dispatch(UPDATE_COUPON_MESSAGE(t('coupons.errorEvent')));
            return;
        }

        let rate = getRate();
        if (rate > parseFloat(balance)) {
            dispatch(UPDATE_COUPON_MESSAGE(t('coupons.newError')));
            return;
        }
        if (isSubmitting.current) {
            console.warn("double click")
            return;
        }
        isSubmitting.current = true;
        //вызов Websocket функции сохранения купона
        try {
            await saveCouponWithWebSocket({
                playerId: (playerInfo.playerId),
                steps: getStepsByCouponType(coupons.currentCouponType),
                acceptType: acceptType
            }, coupons);
        } catch (error) {
            console.error('Error saving coupon via WebSocket:', error);
            dispatch(UPDATE_COUPON_MESSAGE(t('coupons.connectionError') || 'Ошибка подключения'));
        }
    }

    function getStepsByCouponType(couponType: couponType): Array<CouponStepForm> {
        const activeEvents = coupons[couponType].filter(el => el.active);
        const oddsArray = activeEvents.map(el => el.odds).filter((odds): odds is number => {
            if (odds === undefined || odds === null) {
                return false;
            } else {
                return true;
            }
        });
        switch (couponType) {
            case 'ORDINAR':
                return coupons[currentCouponType]
                    .filter(el => el.active)
                    .map((el) => ({
                        events: [el],
                        type: currentCouponType
                    }));
            case 'EXPRESS':
                return [{
                    events: activeEvents,
                    m: activeEvents.length,
                    // при k = (m-1), n будет равно m
                    n: activeEvents.length,
                    odds: Number((
                        events.reduce((total, amount) => {
                            return total * amount.odds
                        }, 1)
                    ).toFixed(2)),
                    rate: Number((coupons.rate[couponType]).toFixed(2)),
                    type: currentCouponType
                }];

            case 'SYSTEM':
                try {
                    return [{
                        events: activeEvents,
                        m: activeEvents.length - 1,
                        // при k = (m-1), n будет равно m
                        n: activeEvents.length,
                        odds: oddsForSystem(oddsArray),
                        rate: Number((coupons.rate[couponType] * activeEvents.length).toFixed(2)),
                        type: currentCouponType
                    }];
                } finally {
                }

        }
    }

    const oddsForSystem = (oddsArray: number[]) => {
        if (oddsArray.length === 0) {
            return 0;
        }
        return oddsArray.length === 1 ? calcOdds(oddsArray) : Number((oddsArray.map((val, index) => {
            const subArray = oddsArray.slice();
            subArray.splice(index, 1);
            return calcOdds(subArray);
        }).reduce((totalOdds, curOdds) => (totalOdds + curOdds)) / oddsArray.length).toFixed(2));
    };

    const calcOdds = (arr: number[]) => arr.reduce((total, current) => total * current);

    const getRate = (): number => {
        switch (currentCouponType) {
            case 'SYSTEM':
                const activeEvents = coupons[currentCouponType].filter(el => el.active);
                return coupons.rate[currentCouponType] * activeEvents.length;
            case 'ORDINAR':
                return coupons[currentCouponType]
                    .filter(el => el.active)
                    .reduce((sum, c) => sum + (c.rate || 0), 0);
            case 'EXPRESS':
                return coupons.rate[currentCouponType];

        }
    };

    const incorrectRate = (): boolean => {
        const rate = getRate();
        return rate === 0 || rate > Number(balance);
    };


    const getMaxApprove = (selectionId?: number): boolean => {
        const rate = getRate();
        const max = getMinOrMaxLimit(currentCouponType, "maxRate", events, Math.min, selectionId);
        if (!max) {
            return false;
        }
        return max < rate;
    };

    React.useEffect(() => {
        let mustApprove = false;
        if (currentCouponType === "ORDINAR") {
            mustApprove = events.some(event => getMaxApprove(event.selectionId));
        } else {
            mustApprove = getMaxApprove();
        }
        setMustApprove(prev => prev ? prev : mustApprove);


    }, [getRate()]);

    return (
        <h5>
            {(maxEraiCoeff || maxEraiResult) &&
                playerInfo.playerId &&
                playerInfo.playerId !== 'GUEST' && (
                    <div className="pre-wrap text-white">
                        {t('coupons.acceptBlock.maxErai')}
                    </div>
                )}
            <div className="grid grid-cols-2 gap-x-2.5 pt-2 desktop:pt-3.5">
                <Button
                    onClick={saveCouponNow}
                    disabled={
                        isSubmitting.current ||
                        maxEraiCoeff ||
                        maxEraiResult ||
                        mustApprove ||
                        incorrectRate() ||
                        isGetLimits
                    }
                    className={
                        maxEraiCoeff ||
                        maxEraiResult ||
                        mustApprove ||
                        incorrectRate()
                            ? 'rounded-[8px] text-[#ABABAB] bg-[#828282B2] px-[19px]'
                            : 'rounded-[8px] bg-[#EACD40] text-black px-[19px] hover:bg-[#EACD40] cursor-pointer'
                    }
                >
                    <span className="text-[12px] desktop:text-[16px] font-['Roboto'] leading-[100%] font-bold uppercase tracking-normal ">
                        {t('coupons.acceptBlock.computeResultButton')}
                    </span>
                </Button>
                <Button
                    disabled={!mustApprove}
                    onClick={() => setMustApprove(false)}
                    className={
                        mustApprove
                            ? 'rounded-[8px] bg-[#EACD40] text-black px-[19px] hover:bg-[#EACD40] cursor-pointer'
                            : 'rounded-[8px] text-[#ABABAB] bg-[#828282B2] px-[19px]'
                    }
                >
                    <span className="text-[12px] desktop:text-[16px] font-['Roboto'] leading-[100%] font-bold uppercase tracking-normal">
                        {t('coupons.acceptBlock.acceptButton')}
                    </span>
                </Button>
                {openModal && <NoAuthOrMoneyModal modalType={ModalType.COUPONS} outsideIsOpen={openModal} outsideClose={() => setOpenModal(false)}/>}
            </div>
        </h5>
    );
}
